// components/AnimatedSection.jsx

// The `react-intersection-observer` library is used to detect when an element enters or leaves the viewport.
// It provides a hook (`useInView`) that simplifies the process of observing elements and triggering animations or actions based on their visibility.

import { motion } from "framer-motion";
import { useInView } from "react-intersection-observer";

// This component, `AnimatedSection`, wraps its children with an animation effect.
// It uses `react-intersection-observer` to detect when the section is in view and triggers a fade-in animation with a slide-up effect.
// The `delay` prop allows customizing the delay before the animation starts.
const AnimatedSection = ({ children, delay = 0 }) => {
  // Use the `useInView` hook to observe the visibility of the element.
  const [ref, inView] = useInView({
    triggerOnce: true, // Ensures the animation triggers only once when the element enters the viewport.
    threshold: 0.1, // The percentage of the element that must be visible to trigger the animation.
  });

  const variants = {
    hidden: { opacity: 0, y: 20 }, // Initial state: hidden and slightly below its final position.
    visible: {
      opacity: 1, // Final state: fully visible.
      y: 0, // Final position: no vertical offset.
      transition: {
        duration: 0.6, // Animation duration in seconds.
        delay: delay, // Delay before the animation starts.
        ease: "easeOut", // Easing function for a smooth effect.
      },
    },
  };

  return (
    <motion.div
      ref={ref} // Attach the `ref` to the element to observe its visibility.
      initial="hidden" // Initial animation state.
      animate={inView ? "visible" : "hidden"} // Trigger animation based on visibility.
      variants={variants} // Define the animation variants.
    >
      {children}
    </motion.div>
  );
};

export default AnimatedSection;